<template>
  <div>
    <DemoNotice v-if="isDemo" />

    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-800 dark:text-gray-100">数据概览</h1>
      <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">查看租户的用户增长、订阅、成交额与佣金数据。</p>
    </div>

    <div class="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
      <div class="rounded-lg border border-gray-100 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-500 dark:text-gray-400">租户用户总数</p>
            <p class="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">{{ formatNumber(stats.totalUsers) }}</p>
          </div>
          <div class="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 dark:bg-indigo-900/40">
            <UsersIcon class="h-6 w-6 text-indigo-600 dark:text-indigo-300" />
          </div>
        </div>
      </div>

      <div class="rounded-lg border border-gray-100 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-500 dark:text-gray-400">本月新增用户</p>
            <p class="mt-2 text-3xl font-bold text-emerald-600 dark:text-emerald-400">{{ formatNumber(stats.newUsersThisMonth) }}</p>
          </div>
          <div class="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-900/40">
            <ArrowTrendingUpIcon class="h-6 w-6 text-emerald-600 dark:text-emerald-300" />
          </div>
        </div>
      </div>

      <div class="rounded-lg border border-gray-100 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-500 dark:text-gray-400">有效订阅用户数</p>
            <p class="mt-2 text-3xl font-bold text-sky-600 dark:text-sky-400">{{ formatNumber(stats.activeSubscribedUsers) }}</p>
          </div>
          <div class="flex h-12 w-12 items-center justify-center rounded-full bg-sky-50 dark:bg-sky-900/40">
            <CreditCardIcon class="h-6 w-6 text-sky-600 dark:text-sky-300" />
          </div>
        </div>
      </div>

      <div class="rounded-lg border border-gray-100 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-sm text-gray-500 dark:text-gray-400">本月成交额</p>
            <p class="mt-2 text-3xl font-bold text-emerald-600 dark:text-emerald-400">{{ formatRevenue(stats.revenueThisMonth) }}</p>
          </div>
          <div class="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-900/40">
            <BanknotesIcon class="h-6 w-6 text-emerald-600 dark:text-emerald-300" />
          </div>
        </div>
      </div>
    </div>

    <!-- Commission Summary -->
    <div class="mb-6">
      <h2 class="mb-4 text-lg font-semibold text-gray-800 dark:text-gray-100">佣金概览</h2>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <div class="rounded-lg border border-gray-100 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-500 dark:text-gray-400">待结算佣金</p>
              <p class="mt-2 text-3xl font-bold text-amber-600 dark:text-amber-400">{{ formatRevenue(commissionStats.pendingCommission) }}</p>
            </div>
            <div class="flex h-12 w-12 items-center justify-center rounded-full bg-amber-50 dark:bg-amber-900/40">
              <BanknotesIcon class="h-6 w-6 text-amber-600 dark:text-amber-300" />
            </div>
          </div>
        </div>

        <div class="rounded-lg border border-gray-100 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-500 dark:text-gray-400">已结算佣金</p>
              <p class="mt-2 text-3xl font-bold text-emerald-600 dark:text-emerald-400">{{ formatRevenue(commissionStats.settledCommission) }}</p>
            </div>
            <div class="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 dark:bg-emerald-900/40">
              <BanknotesIcon class="h-6 w-6 text-emerald-600 dark:text-emerald-300" />
            </div>
          </div>
        </div>

        <div class="rounded-lg border border-gray-100 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-500 dark:text-gray-400">累计有效佣金</p>
              <p class="mt-2 text-3xl font-bold text-indigo-600 dark:text-indigo-400">{{ formatRevenue(commissionStats.totalCommission) }}</p>
            </div>
            <div class="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 dark:bg-indigo-900/40">
              <BanknotesIcon class="h-6 w-6 text-indigo-600 dark:text-indigo-300" />
            </div>
          </div>
        </div>

        <div class="rounded-lg border border-gray-100 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm text-gray-500 dark:text-gray-400">已退款佣金</p>
              <p class="mt-2 text-3xl font-bold text-rose-600 dark:text-rose-400">{{ formatRevenue(commissionStats.refundedCommission) }}</p>
            </div>
            <div class="flex h-12 w-12 items-center justify-center rounded-full bg-rose-50 dark:bg-rose-900/40">
              <BanknotesIcon class="h-6 w-6 text-rose-600 dark:text-rose-300" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-6 xl:grid-cols-2">
      <div class="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
        <div class="mb-4">
          <h2 class="text-lg font-semibold text-gray-800 dark:text-gray-100">用户增长趋势（30天）</h2>
          <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">按用户绑定时间统计每日新增用户数量。</p>
        </div>
        <LineChart :labels="userGrowthLabels" :data="userGrowthData" label="新增用户" />
      </div>

      <div class="rounded-lg bg-white p-5 shadow dark:bg-gray-800">
        <div class="mb-4">
          <h2 class="text-lg font-semibold text-gray-800 dark:text-gray-100">成交额趋势（6个月）</h2>
          <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">按已支付订阅的付款时间统计月度成交额。</p>
        </div>
        <BarChart :labels="revenueLabels" :data="revenueData" label="成交额" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive } from "vue";
import { ArrowTrendingUpIcon, BanknotesIcon, CreditCardIcon, UsersIcon } from "@heroicons/vue/24/outline";

import { getDashboardOverviewAPI, getTenantCommissionSummaryAPI } from "@/api/tenantConsole";
import BarChart from "@/components/charts/BarChart.vue";
import LineChart from "@/components/charts/LineChart.vue";
import DemoNotice from "@/components/DemoNotice.vue";
import { useAuthStore } from "@/stores/auth";

const authStore = useAuthStore();
const isDemo = computed(() => authStore.isDemo);

const overview = reactive({
  stats: {
    totalUsers: 0,
    newUsersThisMonth: 0,
    activeSubscribedUsers: 0,
    revenueThisMonth: 0,
  },
  trends: {
    userGrowth30d: [] as Array<{ date: string; count: number }>,
    revenue6m: [] as Array<{ month: string; revenue: number }>,
  },
});

const commissionStats = reactive({
  totalCommission: 0,
  totalOrders: 0,
  pendingCommission: 0,
  settledCommission: 0,
  refundedCommission: 0,
});

const stats = computed(() => overview.stats);
const userGrowthLabels = computed(() => overview.trends.userGrowth30d.map((item) => item.date.slice(5)));
const userGrowthData = computed(() => overview.trends.userGrowth30d.map((item) => item.count));
const revenueLabels = computed(() => overview.trends.revenue6m.map((item) => item.month.slice(2)));
const revenueData = computed(() => overview.trends.revenue6m.map((item) => Math.round(item.revenue / 100)));

const formatNumber = (value: number) => value.toLocaleString("zh-CN");
const formatRevenue = (value: number) => Math.round(value / 100).toLocaleString("zh-CN");

const loadOverview = async () => {
  const response = await getDashboardOverviewAPI();
  if (response.status !== 200 || !response.data) {
    return;
  }
  overview.stats = response.data.stats;
  overview.trends = response.data.trends;
};

const loadCommissionSummary = async () => {
  const response = await getTenantCommissionSummaryAPI();
  if (response.status !== 200 || !response.data) {
    return;
  }
  Object.assign(commissionStats, response.data);
};

onMounted(() => {
  loadOverview();
  loadCommissionSummary();
});
</script>
